"use client";

import {useSyncExternalStore} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAndroid, faApple} from "@fortawesome/free-brands-svg-icons";
import {InlineText} from "~/components/content/common/InlineText";

export type MobileAppDownloadButtonsDictionary = {
    homeMobileAppDownloadButtonsIosLabel: string;
    homeMobileAppDownloadButtonsAndroidLabel: string;
};

function subscribe() {
    return () => {};
}

function getPlatform(): "ios" | "android" | "desktop" | "unsupported" {
    const {userAgent, platform, maxTouchPoints} = navigator;

    // Some unsupported mobile browsers also include Android or iPhone tokens.
    if (/Windows Phone|IEMobile|BlackBerry|BB10|KaiOS/i.test(userAgent)) {
        return "unsupported";
    }
    if (/iPad|iPhone|iPod/i.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1)) {
        return "ios";
    }
    if (/Android/i.test(userAgent)) {
        return "android";
    }
    const userAgentData = (navigator as Navigator & {userAgentData?: {mobile: boolean}}).userAgentData;
    return userAgentData?.mobile || /Mobile|Tablet|Silk|Kindle|webOS|Opera Mini/i.test(userAgent)
        ? "unsupported"
        : "desktop";
}

function getServerPlatform() {
    return "unsupported" as const;
}

export function MobileAppDownloadButtons({dictionary}: {
    dictionary: MobileAppDownloadButtonsDictionary;
}) {
    const platform = useSyncExternalStore(subscribe, getPlatform, getServerPlatform);
    const platforms = [
        {
            platform: "ios",
            href: "https://apps.apple.com/de/app/docid/id6463717932",
            icon: faApple,
            label: dictionary.homeMobileAppDownloadButtonsIosLabel,
        },
        {
            platform: "android",
            href: "https://play.google.com/store/apps/details?id=com.docid.app",
            icon: faAndroid,
            label: dictionary.homeMobileAppDownloadButtonsAndroidLabel,
        },
    ];

    const visiblePlatforms = platforms.filter((item) => platform === "desktop" || item.platform === platform);

    if (visiblePlatforms.length === 0) {
        return null;
    }

    return <div className="flex w-full flex-row gap-4">{visiblePlatforms.map(({href, icon, label}) => (
        <a
            key={href}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-w-0 flex-1 items-center justify-center gap-x-3 rounded-lg py-4 px-6 text-normal font-jost font-semibold transition-colors text-zinc-900 bg-white hover:bg-zinc-100 active:bg-zinc-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
            <FontAwesomeIcon size="sm" icon={icon} aria-hidden="true" className="inline-block relative shrink-0" />
            <span className="min-w-0 text-center">
                <InlineText value={label} />
            </span>
        </a>
    ))}</div>;
}
